// Shared math for turning a wake's raw ultrasonic echo array (d1_raw_us,
// microseconds, from the /raw-samples backend endpoint) into distance
// statistics in cm. Mirrors the firmware's own aggregation
// (padihat-firmware-v0.3/firmware/src/main.cpp, sampling block around
// main.cpp:1118-1211) so client-side stats are directly comparable to the
// firmware's own d1/d1_cal aggregate.

// Firmware plausibility gate (padihat-firmware-v0.3/firmware/include/config.h:158-160).
export const DISTANCE_SIGMA_MULT = 2.0;
export const DISTANCE_MIN_VALID_MM = 30;
export const DISTANCE_MAX_VALID_MM = 5000;

export const AGGREGATE_METHODS = ["mean", "median", "min", "max", "sigma_mean"];

export function speedOfSound(ext_t, ext_h) {
  if (typeof ext_t !== "number" || !isFinite(ext_t)) return 343;
  const rh = typeof ext_h === "number" && isFinite(ext_h) ? ext_h : 0;
  return 331.3 * Math.sqrt(1 + ext_t / 273.15) + 0.0124 * rh;
}

// us -> cm, round-trip, given speed of sound in m/s.
export function usToCm(us, c_ms) {
  return (us * c_ms) / 2 / 10000;
}

// Drop the ultrasonic sensor's timeout/no-echo sentinel (0us) plus any
// non-finite values. A 0us reading isn't "very close", it's "no echo" -
// firmware never even records these as samples (main.cpp's collect loop
// `continue`s past a timeout without appending to dur_us[]), so including
// them here biases min/median toward zero in a way firmware's own d1/d1_cal
// never sees.
export function validSamples(rawUs) {
  if (!Array.isArray(rawUs)) return [];
  return rawUs.filter((us) => typeof us === "number" && isFinite(us) && us > 0);
}

// Mirrors firmware's two-pass sigma filter exactly (main.cpp:1162-1180):
// mean + sample stddev (n-1), then drop samples further than `mult`
// stddevs from the mean. sd===0 (or n<=1) keeps everything, same as
// firmware's `if (sd == 0.0f || ...)` guard.
export function sigmaFilter(values, mult = DISTANCE_SIGMA_MULT) {
  const n = values.length;
  if (n === 0) return [];
  if (n === 1) return values.slice();
  const m = values.reduce((a, b) => a + b, 0) / n;
  const variance = values.reduce((a, b) => a + (b - m) ** 2, 0) / (n - 1);
  const sd = Math.sqrt(variance);
  if (sd === 0) return values.slice();
  const cutoff = mult * sd;
  return values.filter((v) => Math.abs(v - m) <= cutoff);
}

function median(sortedValues) {
  const n = sortedValues.length;
  if (n === 0) return null;
  const mid = (n - 1) >> 1;
  return n % 2 ? sortedValues[mid] : (sortedValues[n / 2 - 1] + sortedValues[n / 2]) / 2;
}

function mean(values) {
  if (values.length === 0) return null;
  return values.reduce((a, b) => a + b, 0) / values.length;
}

// Firmware's plausibility gate (main.cpp:1194,1198): distances outside
// [DISTANCE_MIN_VALID_MM, DISTANCE_MAX_VALID_MM] are dropped, not clamped.
function applyGate(cm) {
  if (cm == null) return null;
  const mm = cm * 10;
  return mm >= DISTANCE_MIN_VALID_MM && mm <= DISTANCE_MAX_VALID_MM ? cm : null;
}

/**
 * Turn one wake's raw sample row into a single distance statistic in cm.
 * @param {{d1_raw_us?: number[], ext_t?: number, ext_h?: number}} row
 * @param {"mean"|"median"|"min"|"max"|"sigma_mean"} method
 * @returns {{value_cm: number, n_valid: number, n_total: number} | null}
 */
export function aggregateWake(row, method) {
  const rawUs = row?.d1_raw_us;
  const nTotal = Array.isArray(rawUs) ? rawUs.length : 0;
  const valid = validSamples(rawUs);
  if (valid.length === 0) return null;

  const source = method === "sigma_mean" ? sigmaFilter(valid) : valid;
  if (source.length === 0) return null;

  let us;
  if (method === "min") {
    us = Math.min(...source);
  } else if (method === "max") {
    us = Math.max(...source);
  } else if (method === "median") {
    us = median([...source].sort((a, b) => a - b));
  } else {
    // "mean" and "sigma_mean" both average their (possibly filtered) source.
    us = mean(source);
  }
  if (us == null) return null;

  const c_ms = speedOfSound(row.ext_t, row.ext_h);
  const value_cm = applyGate(usToCm(us, c_ms));
  if (value_cm == null) return null;
  return { value_cm, n_valid: source.length, n_total: nTotal };
}
